// ============================================================================
// PairsForm — Attribution du partage (§8 animateur-ambassadeur)
// ============================================================================
// Le lien partagé par l'animateur porte `?via=<ref>`. On le capte en cookie
// première-partie (30 j) à l'arrivée sur la fiche, puis on le recopie dans
// `inscriptions.sourceRef` à la pré-inscription. Pas de pixel tiers, anonyme
// avant inscription. Socle commun avec l'attribution partenaires / codes promo.
// ============================================================================

const VIA_COOKIE = "pf_via";
const MAX_AGE_DAYS = 30;

/** Capte `?via=` depuis l'URL et le persiste 30 j (cookie 1ʳᵉ partie). */
export function captureViaFromUrl(search: string): void {
  try {
    const via = new URLSearchParams(search).get("via");
    if (via && /^[\w-]{1,64}$/.test(via)) {
      const expires = new Date(Date.now() + MAX_AGE_DAYS * 86_400_000).toUTCString();
      document.cookie = `${VIA_COOKIE}=${encodeURIComponent(via)}; expires=${expires}; path=/; SameSite=Lax`;
    }
  } catch {
    /* cookies indisponibles (mode privé) — l'attribution est best-effort */
  }
}

/** Renvoie la réf d'attribution mémorisée, ou null. */
export function getVia(): string | null {
  try {
    const m = document.cookie.match(/(?:^|;\s*)pf_via=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

// ============================================================================
// Attribution marketing (UTM / referrer / page d'atterrissage) — trou 3 du
// séquençage de lancement (2026-08-07) : à instrumenter AVANT toute
// communication, ces données ne se rattrapent pas. First-touch en
// localStorage, recopiée dans `inscriptions.attribution` à la pré-inscription.
// ============================================================================

const ATTR_KEY = "pf_attribution";

interface AttributionMarketing {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  referrer?: string;
  landingPage?: string;
  capturedAt?: string;
}

const borne = (v: string | null): string | undefined =>
  v && v.trim() ? v.trim().slice(0, 300) : undefined;

/**
 * Capte les UTM + referrer + page d'atterrissage au chargement de l'app.
 * First-touch : on ne remplace jamais une capture existante, SAUF si elle
 * n'avait aucun UTM et que la visite courante en porte (une première visite
 * directe ne doit pas masquer pour toujours la campagne qui convertit).
 */
export function captureAttributionFromUrl(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const courante: AttributionMarketing = {
      utmSource: borne(params.get("utm_source")),
      utmMedium: borne(params.get("utm_medium")),
      utmCampaign: borne(params.get("utm_campaign")),
      utmContent: borne(params.get("utm_content")),
      referrer: borne(document.referrer && !document.referrer.includes(window.location.host) ? document.referrer : null),
      landingPage: borne(window.location.pathname),
      capturedAt: new Date().toISOString(),
    };
    const aDesUtm = !!(courante.utmSource || courante.utmMedium || courante.utmCampaign);

    const brut = localStorage.getItem(ATTR_KEY);
    if (brut) {
      const existante = JSON.parse(brut) as AttributionMarketing;
      const existanteAvaitUtm = !!(existante.utmSource || existante.utmMedium || existante.utmCampaign);
      if (existanteAvaitUtm || !aDesUtm) return; // first-touch conservé
    } else if (!aDesUtm && !courante.referrer) {
      // Visite directe sans referrer externe : rien d'attribuable à mémoriser.
      return;
    }
    localStorage.setItem(ATTR_KEY, JSON.stringify(courante));
  } catch {
    /* best-effort — jamais bloquant */
  }
}

/** Renvoie l'attribution marketing mémorisée, ou null. */
export function getAttribution(): AttributionMarketing | null {
  try {
    const brut = localStorage.getItem(ATTR_KEY);
    return brut ? (JSON.parse(brut) as AttributionMarketing) : null;
  } catch {
    return null;
  }
}
